// «Конструктор» of the site (stage I3, R-W1 «простой и красивый»), laid out as the form builder (um/2026-10-28/06): pages of a site in
// the list column; a page has a header with its state and «Опубликовать», and tabs — «Блоки» (rows that open to their parameters,
// like the fields of a form), «Предпросмотр» (the page as the site draws it, before saving) and «Настройки» (address, menu).

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { readBlocks, uuidv7, type Id, type PageBlock, type RecordObj } from '@confdeck/core-shared';
import { useDebounced, useMeta, useSync, useTypesTick } from '../sync/react.tsx';
import { setField } from '../sync/mutations.ts';
import { navigate } from '../shell/router.ts';
import { leaveThen, useUnsaved } from '../shell/unsaved.ts';
import { useScreenSearch } from '../shell/screen-search.ts';
import { blockSummary, pathProblem } from './builder-text.ts';
import { IconButton, Button, Chip, Dialog, Input, Menu, MenuContent, MenuItem, MenuTrigger, Segmented, Switch, Tabs, cx, useToast, Select, Field } from '../ui/primitives.tsx';
import { Icon } from '../ui/icons.tsx';
import { ListBar, ListBody, ListEmpty, ListHead, ListReader, ListRow } from '../ui/ListReader.tsx';
import { TopBar, TopTitleInput } from '../ui/TopBar.tsx';
import { BlockParams } from './BlockParams.tsx';
import { TEMPLATES } from './templates.ts';
import f from '../ui/builder.module.css';
import s from './sites.module.css';

const str = (v: unknown) => (v == null ? '' : String(v));
const query = () => new URLSearchParams(location.search);
const remember = (key: string, v: string | null) => { const q = query(); if (v) q.set(key, v); else q.delete(key); navigate(`${location.pathname}?${q}`, { replace: true }); };
type Tab = 'blocks' | 'preview' | 'settings';

export function Builder() {
  const client = useSync();
  const meta = useMeta();
  const pageType = meta.types().find((t) => t.app === 'site' && t.key === 'page');
  const siteType = meta.types().find((t) => t.app === 'site' && t.key === 'site');
  useTypesTick([pageType?.id, siteType?.id].filter((x): x is Id => !!x));
  const live = (typeId?: Id) => (typeId ? [...client.store.recordIds(typeId)].map((id) => client.store.record(id)).filter((r): r is RecordObj => !!r && !r.archivedAt) : []);
  // The main site is the oldest one; a page without a site is its page.
  const sites = live(siteType?.id).sort((a, b) => a.createdAt.localeCompare(b.createdAt) || a.id.localeCompare(b.id));
  const siteField = pageType ? meta.field(pageType.id, 'site') : undefined;
  // A link to a page (?page=) opens the page's own site.
  const [siteId, setSiteId] = useState<Id | null>(() => {
    const asked = client.store.record(query().get('page') ?? '');
    return query().get('site') ?? (asked && siteField ? client.store.linked(asked, siteField)[0] ?? null : null);
  });
  const site = sites.find((x) => x.id === siteId) ?? sites[0];
  const pages = live(pageType?.id).filter((p) => { const own = siteField ? client.store.linked(p, siteField)[0] : undefined; return own ? own === site?.id : site?.id === sites[0]?.id; })
    .sort((a, b) => str(a.data.path).localeCompare(str(b.data.path)));
  const [pageId, setPageId] = useState<Id | null>(() => query().get('page'));
  const page = pages.find((p) => p.id === pageId) ?? pages[0];
  const [creating, setCreating] = useState(false);
  // Another page or site opens through the core's question about unsaved work (leaveThen), as leaving the section does.
  useEffect(() => remember('site', site?.id ?? null), [site?.id]);
  useEffect(() => remember('page', page?.id ?? null), [page?.id]);

  // The global search looks here first (R-U23): the pages of the open site by the title and the address.
  useScreenSearch((query) => {
    const needle = query.trim().toLowerCase();
    return pages.filter((p) => `${str(p.data.title)} ${str(p.data.path)}`.toLowerCase().includes(needle))
      .map((p) => ({ id: p.id, recordId: p.id, label: str(p.data.title) || 'Без названия', hint: str(p.data.path) || 'без адреса', icon: 'globe', run: () => leaveThen(() => setPageId(p.id)) }));
  });

  if (!pageType || !siteType) return <p className={s.empty}>Раздел «Сайт» недоступен.</p>;
  return (
    <ListReader selection={page?.id ?? null} listLabel="Страницы" readerLabel="Страница" list={(
      <>
        <ListHead title="Страницы" add={{ label: 'Страница', onClick: () => setCreating(true) }} />
        {sites.length > 1 && <ListBar><Select label="Сайт" className={s.siteSelect} value={site?.id ?? ''} onChange={(v) => leaveThen(() => { setSiteId(v); setPageId(null); })} options={sites.map((x, i) => ({ value: x.id, label: `${str(x.data.name)}${i === 0 ? ' — основной' : ''}` }))} /></ListBar>}
        {pages.length ? (
          <ListBody label="Страницы">
            {pages.map((p) => (
              <ListRow key={p.id} selected={p.id === page?.id} onClick={() => leaveThen(() => setPageId(p.id))} dot={p.data.published_version ? 'on' : 'off'}
                title={str(p.data.title) || 'Без названия'} sub={`${str(p.data.path) || 'без адреса'} · ${p.data.published_version ? 'опубликована' : 'черновик'}${p.data.in_menu ? ' · в меню' : ''}`} />
            ))}
          </ListBody>
        ) : <ListEmpty icon="globe" text="Страниц пока нет" hint="Создайте первую страницу кнопкой «+»." />}
        <NewPage open={creating} onClose={() => setCreating(false)} siteId={site?.id ?? null} pageTypeId={pageType.id} onMade={(id) => { setCreating(false); leaveThen(() => setPageId(id)); }} />
      </>
    )} reader={page ? <PageEditor key={page.id} page={page} mainSite={site?.id === sites[0]?.id} pageTypeId={pageType.id} siteId={site?.id ?? null} onOpen={(id) => leaveThen(() => setPageId(id))} />
      : <><TopBar /><ListEmpty icon="globe" text="Создайте первую страницу сайта" /></>} />
  );
}

function PageEditor({ page, mainSite, pageTypeId, siteId, onOpen }: { page: RecordObj; mainSite: boolean; pageTypeId: Id; siteId: Id | null; onOpen: (id: Id) => void }) {
  const client = useSync();
  const meta = useMeta();
  const toast = useToast();
  const defs = useMemo(() => client.header?.blocks ?? [], [client.header]);
  const byKey = useMemo(() => new Map(defs.map((d) => [d.key, d])), [defs]);
  const [tab, setTab] = useState<Tab>('blocks');
  const [blocks, setBlocks] = useState<PageBlock[]>(() => readBlocks(page.data.blocks));
  const [dirty, setDirty] = useState(false);
  // J2: «Отменить» — the states of the page's blocks before each change, the last 30.
  const [past, setPast] = useState<PageBlock[][]>([]);
  const [open, setOpenBlock] = useState<string | null>(null);
  const [title, setTitle] = useState(str(page.data.title));
  useEffect(() => setTitle(str(page.data.title)), [page.data.title]);
  // A page changed elsewhere: the editor follows it unless it holds unsaved work.
  useEffect(() => { if (!dirty) setBlocks(readBlocks(page.data.blocks)); }, [page.data.blocks]);

  const change = (next: PageBlock[]) => { setPast((p) => [...p.slice(-29), blocks]); setBlocks(next); setDirty(true); };
  const undo = () => { const prev = past.at(-1); if (!prev) return; setPast(past.slice(0, -1)); setBlocks(prev); setDirty(true); };
  // Ctrl+Z outside of a field undoes the last change of the blocks.
  useEffect(() => {
    const on = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null;
      if ((e.ctrlKey || e.metaKey) && !e.shiftKey && e.key.toLowerCase() === 'z' && !(t && /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName))) { e.preventDefault(); undo(); }
    };
    window.addEventListener('keydown', on);
    return () => window.removeEventListener('keydown', on);
  });
  const add = (key: string) => {
    const def = byKey.get(key);
    if (!def) return;
    const b: PageBlock = { id: uuidv7(), key, params: Object.fromEntries(def.params.filter((p) => p.default !== undefined).map((p) => [p.key, p.default])) };
    change([...blocks, b]);
    setOpenBlock(b.id);
  };
  const move = (i: number, d: -1 | 1) => { const j = i + d; if (j < 0 || j >= blocks.length) return; const next = [...blocks]; [next[i], next[j]] = [next[j]!, next[i]!]; change(next); };
  const moveTo = (from: number, to: number) => { const next = [...blocks]; const [x] = next.splice(from, 1); next.splice(to > from ? to - 1 : to, 0, x!); change(next); };
  const duplicate = (i: number) => { const b = blocks[i]!; const copy = { ...b, id: uuidv7(), params: { ...b.params } }; change([...blocks.slice(0, i + 1), copy, ...blocks.slice(i + 1)]); setOpenBlock(copy.id); };
  const setParam = (id: string, key: string, v: unknown) => change(blocks.map((b) => (b.id === id ? { ...b, params: { ...b.params, [key]: v } } : b)));

  const save = async () => {
    const r = await client.run('page.saveBlocks', { id: page.id, blocks });
    if (!r.ok) { toast({ kind: 'error', text: r.error.message }); return false; }
    setDirty(false);
    return true;
  };
  // Unsaved blocks are not lost by the rail, the column, «назад» or closing the tab: the core asks first.
  useUnsaved(dirty, save, `страница «${str(page.data.title) || 'Без названия'}»`);
  const publish = async () => {
    if (dirty && !(await save())) return;
    const r = await client.run('content.publish', { id: page.id });
    toast(r.ok ? { kind: 'info', text: 'Опубликовано: страница на сайте обновлена' } : { kind: 'error', text: r.error.message });
  };
  const copy = async () => {
    const id = uuidv7();
    const r = await client.run('record.create', { id, typeId: pageTypeId, data: { title: `${str(page.data.title)} (копия)`, path: `${str(page.data.path).replace(/\/\*$/, '')}-copy`, ...(siteId ? { site: [siteId] } : {}) } });
    if (!r.ok) return toast({ kind: 'error', text: r.error.message });
    await client.run('page.saveBlocks', { id, blocks: blocks.map((b) => ({ ...b, id: uuidv7() })) });
    onOpen(id);
  };
  const rename = () => { const t = title.trim(); if (t && t !== str(page.data.title)) setField(client, page, 'title', t); };
  const siteUrl = client.header?.siteUrl ?? null;
  const path = str(page.data.path);
  const onSite = siteUrl && mainSite && path && !path.includes('*') ? `${siteUrl}${path}` : null;
  const published = !!page.data.published_version;

  return (
    <section className={f.editor}>
      <TopBar start={<>
        <TopTitleInput value={title} onChange={setTitle} onCommit={rename} label="Название страницы" />
        <Chip color={published ? 'green' : 'gray'} dot className={f.noShrink}>{published ? 'Опубликована' : 'Черновик'}</Chip>
        <span className={f.typeNote}>{path || 'без адреса'}</span>
        {dirty && <span className={f.dirtyNote}>· есть несохранённые изменения</span>}
      </>}
        actions={[
          ...(onSite ? [{ key: 'site', label: 'На сайте', icon: 'external', onClick: () => window.open(onSite, '_blank') }] : []),
          { key: 'undo', label: 'Отменить', icon: 'undo', disabled: !past.length, title: 'Ctrl+Z', onClick: undo },
          { key: 'publish', label: 'Опубликовать', icon: 'tick', primary: true, onClick: () => void publish() },
        ]}
        more={<MenuItem icon="copy" onSelect={() => void copy()}>Копировать страницу</MenuItem>} />
      <Tabs bar label="Страница сайта" value={tab} onChange={setTab} items={[{ key: 'blocks', label: 'Блоки' }, { key: 'preview', label: 'Предпросмотр' }, { key: 'settings', label: 'Настройки' }]} />
      <div className={f.scroll}>
      {tab === 'blocks' && (
        <div className={f.builder}>
          <section className={f.step}>
            <div className={f.stepHead}><span className={f.stepNo}>Страница</span><span className={f.stepTitle}>Блоки сверху вниз</span></div>
            <div className={f.fields} onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); const from = e.dataTransfer.getData('cd/move'); if (from !== '') moveTo(Number(from), blocks.length); }}>
              {blocks.map((b, i) => {
                const def = byKey.get(b.key);
                const expanded = open === b.id;
                return (
                  <div key={b.id} className={cx(f.field, expanded && f.fieldOpen)} draggable onDragStart={(e) => e.dataTransfer.setData('cd/move', String(i))}
                    onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); e.stopPropagation(); const from = e.dataTransfer.getData('cd/move'); if (from !== '') moveTo(Number(from), i); }}>
                    <div className={f.fieldRow}>
                      <span className={f.kind}>{def?.name ?? b.key}</span>
                      <button type="button" className={s.rowText} onClick={() => setOpenBlock(expanded ? null : b.id)}>{blockSummary(meta, b) || def?.description || ''}</button>
                      <IconButton size="sm" icon="chevronUp" label="Вверх" disabled={i === 0} onClick={() => move(i, -1)} />
                      <IconButton size="sm" icon="chevronDown" label="Вниз" disabled={i === blocks.length - 1} onClick={() => move(i, 1)} />
                      <IconButton size="sm" icon="settings" label="Параметры блока" pressed={expanded} onClick={() => setOpenBlock(expanded ? null : b.id)} />
                      <IconButton size="sm" icon="copy" label="Дублировать блок" onClick={() => duplicate(i)} />
                      <IconButton size="sm" icon="trash" label="Убрать блок" onClick={() => { change(blocks.filter((x) => x.id !== b.id)); if (open === b.id) setOpenBlock(null); }} />
                    </div>
                    {expanded && def && <div className={f.fieldMore}><BlockParams block={b} def={def} onChange={(k, v) => setParam(b.id, k, v)} /></div>}
                  </div>
                );
              })}
              {!blocks.length && <p className={f.muted}>На странице пока нет блоков — «Добавить блок».</p>}
            </div>
            <Menu>
              <MenuTrigger asChild><Button size="sm" variant="ghost" icon="plus" className={f.addField}>Добавить блок</Button></MenuTrigger>
              <MenuContent>{defs.map((d) => <MenuItem key={d.key} onSelect={() => add(d.key)}>{d.name}</MenuItem>)}</MenuContent>
            </Menu>
          </section>
          <div className={f.builderFoot}>
            <span className={f.spacer} />
            {dirty && <Button size="sm" variant="ghost" onClick={() => { setBlocks(readBlocks(page.data.blocks)); setDirty(false); setPast([]); }}>Сбросить изменения</Button>}
            <Button size="sm" variant="secondary" disabled={!dirty} onClick={() => void save().then((ok) => ok && toast({ kind: 'info', text: 'Черновик сохранён' }))}>Сохранить черновик</Button>
          </div>
        </div>
      )}
      {tab === 'preview' && <Preview page={page} blocks={blocks} selected={open} onPick={(id) => { setOpenBlock(id); setTab('blocks'); }} />}
      {tab === 'settings' && <PageSettings page={page} />}
      </div>
    </section>
  );
}

/** The page as the site draws it now, unsaved blocks included; a click on a block opens it in «Блоки». */
function Preview({ page, blocks, selected, onPick }: { page: RecordObj; blocks: PageBlock[]; selected: string | null; onPick: (id: string) => void }) {
  const [html, setHtml] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [wide, setWide] = useState(true);
  const shown = useDebounced(blocks, 350);
  const frame = useRef<HTMLIFrameElement>(null);
  // The canvas keeps the real width of the page (1280 for a computer, 390 for a phone) and is scaled down to fit.
  const wrap = useRef<HTMLDivElement>(null);
  const [box, setBox] = useState({ w: 0, h: 0 });
  useEffect(() => {
    const el = wrap.current;
    if (!el) return;
    const ro = new ResizeObserver(([e]) => setBox({ w: e!.contentRect.width, h: e!.contentRect.height }));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const target = wide ? 1280 : 390;
  const scale = box.w ? Math.min(1, box.w / target) : 1;
  useEffect(() => {
    let gone = false;
    void fetch(`/api/pages/${page.id}/canvas`, { method: 'POST', credentials: 'same-origin', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ blocks: shown }) })
      .then(async (r) => { const j = (await r.json()) as { html?: string; error?: { message?: string } }; if (!r.ok || !j.html) throw new Error(j.error?.message ?? `ответ ${r.status}`); return j.html; })
      .then((h) => { if (!gone) { setHtml(h); setError(null); } })
      .catch((e: Error) => { if (!gone) setError(e.message); });
    return () => { gone = true; };
  }, [page.id, shown]);
  // What the frame shows. A new document is given to the frame only after the previous one loaded: a srcdoc changed while the
  // frame is still loading its first document lost the race in Chromium and left the canvas empty (1 run of 3–5 in e2e). The frame
  // is first made with its document in place, never with an empty one.
  const [doc, setDoc] = useState('');
  const loading = useRef(false);
  const pending = useRef<string | null>(null);
  useEffect(() => {
    if (!html) return;
    if (loading.current) { pending.current = html; return; }
    loading.current = true;
    setDoc(html);
  }, [html]);
  const outline = useCallback(() => frame.current?.contentWindow?.postMessage({ cdSelect: selected, scroll: true }, '*'), [selected]);
  const loaded = () => {
    loading.current = false;
    const next = pending.current;
    pending.current = null;
    if (next !== null && next !== doc) { loading.current = true; setDoc(next); return; }
    outline();
  };
  useEffect(() => {
    const on = (e: MessageEvent) => { if (e.source === frame.current?.contentWindow && typeof e.data?.cdBlock === 'string') onPick(e.data.cdBlock); };
    window.addEventListener('message', on);
    return () => window.removeEventListener('message', on);
  }, [onPick]);
  useEffect(outline, [outline]);
  return (
    <div className={s.preview}>
      <div className={s.previewBar}>
        <Segmented size="sm" label="Ширина предпросмотра" value={wide ? 'wide' : 'phone'} onChange={(v) => setWide(v === 'wide')}
          items={[{ value: 'wide', label: 'Компьютер', icon: 'monitor' }, { value: 'phone', label: 'Телефон', icon: 'phone' }]} />
        <span className={f.muted}>Нажмите на блок, чтобы открыть его параметры</span>
      </div>
      {error && <p className={s.error} role="alert">Предпросмотр не собрался: {error}</p>}
      <div className={s.canvasWrap} ref={wrap}>
        {doc ? (
          <iframe ref={frame} title="Страница в конструкторе" className={s.canvas} srcDoc={doc} sandbox="allow-scripts" onLoad={loaded}
            style={{ width: target, height: box.h ? box.h / scale : undefined, transform: `scale(${scale})` }} />
        ) : !error && <p className={s.empty}>Собираем страницу…</p>}
      </div>
    </div>
  );
}

/** «Настройки» of a page: its address and whether it is in the site's menu. */
function PageSettings({ page }: { page: RecordObj }) {
  const client = useSync();
  const [path, setPath] = useState(str(page.data.path));
  useEffect(() => setPath(str(page.data.path)), [page.data.path]);
  const problem = pathProblem(path);
  return (
    <div className={f.settings}>
      <label className={f.row}><span>Адрес</span><span className={s.settingStack}><Input value={path} aria-label="Адрес страницы" onChange={(e) => setPath(e.target.value)}
        onBlur={() => { if (!problem && path !== str(page.data.path)) setField(client, page, 'path', path); }} /><span className={problem ? s.error : s.help}>{problem ?? 'Например, /about. Шаблон страниц всех событий — /e/*'}</span></span></label>
      <div className={f.row}><span>Меню сайта</span><Switch checked={!!page.data.in_menu} onChange={(v) => setField(client, page, 'in_menu', v)} label="В меню сайта">{page.data.in_menu ? 'в меню' : 'не в меню'}</Switch></div>
    </div>
  );
}

/** «Новая страница»: a title, an address and a template of blocks. */
function NewPage({ open, onClose, siteId, pageTypeId, onMade }: { open: boolean; onClose: () => void; siteId: Id | null; pageTypeId: Id; onMade: (id: Id) => void }) {
  const client = useSync();
  const toast = useToast();
  const [title, setTitle] = useState('');
  const [path, setPath] = useState('/');
  const [tpl, setTpl] = useState(TEMPLATES[0]!.key);
  const [inMenu, setInMenu] = useState(true);
  const problem = pathProblem(path);
  const make = async () => {
    if (problem) return;
    const id = uuidv7();
    const r = await client.run('record.create', { id, typeId: pageTypeId, data: { title, path, in_menu: inMenu, ...(siteId ? { site: [siteId] } : {}) } });
    if (!r.ok) return toast({ kind: 'error', text: r.error.message });
    const blocks = TEMPLATES.find((t) => t.key === tpl)!.blocks.map((b) => ({ id: uuidv7(), key: b.key, params: b.params ?? {} }));
    const known = new Set((client.header?.blocks ?? []).map((d) => d.key));
    if (blocks.length) await client.run('page.saveBlocks', { id, blocks: blocks.filter((b) => known.has(b.key)) });
    setTitle(''); setPath('/');
    onMade(id);
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()} label="Новая страница">
      <form className={s.dialogForm} onSubmit={(e) => { e.preventDefault(); void make(); }}>
        <h2 className={s.dialogTitle}>Новая страница</h2>
        <Field label="Название"><Input value={title} onChange={(e) => setTitle(e.target.value)} required aria-label="Название страницы" /></Field>
        <Field label="Адрес"><Input value={path} onChange={(e) => setPath(e.target.value)} placeholder="/about" required aria-label="Адрес страницы" /><span className={problem ? s.error : s.help}>{problem ?? 'Например, /about. Шаблон страниц всех событий — /e/*'}</span></Field>
        <Switch checked={inMenu} onChange={setInMenu} label="Поставить в меню сайта">Поставить в меню сайта</Switch>
        <Field label="Шаблон">
          <Select label="Шаблон страницы" value={tpl} onChange={setTpl} options={TEMPLATES.map((t) => ({ value: t.key, label: t.name }))} />
        </Field>
        <div className={s.bar}><Button type="button" onClick={onClose}>Отмена</Button><Button type="submit" variant="primary" disabled={!!problem}>Создать</Button></div>
      </form>
    </Dialog>
  );
}

/** The «Конструктор» tab of a page card: the page opens in the builder, where it is edited. */
export function OpenInBuilder({ recordId }: { recordId: Id }) {
  return (
    <div className={s.editor}>
      <p className={s.help}>Страница собирается из блоков в конструкторе сайта: блоки перетаскиваются, страница видна сразу такой, какой её покажет сайт.</p>
      <div className={s.bar}><a className={s.open} href={`/s/builder?page=${recordId}`}><Icon name="columns" size={13} /> Открыть в конструкторе</a></div>
    </div>
  );
}
